import React, { useState, useEffect } from 'react';
import queryString from 'query-string';
import Cookies from 'js-cookie';
import styled from 'styled-components';
import { navigate } from 'gatsby';
import Helmet from 'react-helmet';
import SEO, { createSEO } from 'components/Core/SEO';
import SubNav from 'components/Page/Toolbox/SubNav';
import { getUser, stLogin, forgotPassword } from 'services/userService';
import LoginModal from 'components/Page/Toolbox/LoginModal';
import SignupModal from 'components/Page/Toolbox/SignupModal';
import userToolBoxJwt from 'hooks/useToolboxStorage';
import UserContext from 'utils/UserContext';
import WebsiteLayout from 'layouts/websiteLayout';
import toolboxDetailSubNav from 'components/Page/Toolbox/data/toolboxDetailSubNav';
import snackbar from 'utils/snackbar';
import LocationWrapper from 'components/Core/LocationWrapper';
import pollForDefinition from 'utils/pollForDefinition';

const ToolboxLayout = ({
  children,
  backgroundColor,
  theme = '',
  contentfulSeo,
  onSetUserInfo = () => {},
  ...props
}) => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [userInfo, setUserInfo] = useState(null);

  const getAuthClient = () =>
    typeof window === 'object' &&
    window.OktaAuth &&
    new window.OktaAuth({
      issuer: 'https://servicetitan-customers.okta.com/oauth2/default',
      clientId: '0oa4muy79oHWfLUTe5d6',
      redirectUri: `${props.location.origin}/toolbox`,
    });

  const openLoginModal = () => {
    setIsLoginModalOpen(true);
    setIsSignupModalOpen(false);
  };

  const openSignupModal = () => {
    setIsLoginModalOpen(false);
    setIsSignupModalOpen(true);
  };

  const handleCloseAuthModal = () => {
    setIsLoginModalOpen(false);
    setIsSignupModalOpen(false);
  };

  const getUserInfo = async () => {
    const jwt = userToolBoxJwt.get();

    if (!userInfo && jwt) {
      if (userToolBoxJwt.get('ToolboxUserInfo')) {
        setUserInfo(JSON.parse(userToolBoxJwt.get('ToolboxUserInfo')));
        return;
      }
      try {
        const user = await getUser(jwt);
        setUserInfo({ ...user });
      } catch {
        setUserInfo(null);
        userToolBoxJwt.remove();
        userToolBoxJwt.remove('ToolboxUserInfo');
      }
    }
  };

  const auth = (email, password) => {
    const authClient = getAuthClient();
    authClient
      .signInWithCredentials({
        username: email,
        password,
      })
      .then(
        (transaction) => {
          if (transaction.status === 'SUCCESS') {
            handleCloseAuthModal();

            authClient.token
              .getWithoutPrompt({
                responseType: 'id_token',
                sessionToken: transaction.sessionToken,
              })
              .then(({ tokens }) => {
                authClient.tokenManager.setTokens(tokens);
                userToolBoxJwt.set(tokens.idToken.idToken);
                getUserInfo().then();

                const dataLayerData = {
                  event: 'login event',
                  form_type: 'okta',
                  email,
                };

                dataLayer.push(dataLayerData);
              });
          }
        },
        () => {
          setErrorMessage('Authentication failed.');
        },
      );
  };

  const socialAuth = (idp) => {
    Cookies.set('redirect', document.location.pathname);

    const authClient = getAuthClient();

    authClient.token.getWithRedirect({
      clientId: '0oacjbje3HDzZydxA5d6',
      idp,
      responseType: 'code',
      responseMode: ['fragment'],
      idpScope: ['openid', 'email'],
      prompt: 'consent',
      scopes: ['openid'],
    });
  };

  useEffect(() => {
    if (userInfo) {
      userToolBoxJwt.set(JSON.stringify(userInfo), 'ToolboxUserInfo');
      onSetUserInfo(userInfo);
    }
  }, [userInfo]);

  useEffect(() => {
    const { jwt } = queryString.parse(window.location.search);
    const redirect = Cookies.get('redirect');

    if (jwt) {
      pollForDefinition(window, 'OktaAuth', () => {
        const authClient = getAuthClient();

        stLogin(jwt).then(({ sessionToken }) => {
          authClient.token
            .getWithoutPrompt({
              responseType: 'id_token',
              sessionToken,
            })
            .then(({ tokens }) => {
              authClient.tokenManager.setTokens(tokens);
              userToolBoxJwt.set(tokens.idToken.idToken);
              const dataLayerData = {
                event: 'login event',
                form_type: 'service titan',
              };

              dataLayer.push(dataLayerData);
              getUserInfo().then(() => {
                Cookies.remove('redirect');
                navigate(redirect || '/toolbox');
              });
            });
        });
      });
    }
  }, []);

  useEffect(() => {
    const redirect = Cookies.get('redirect');
    const { hash } = window.location;

    pollForDefinition(window, 'OktaAuth', () => {
      const authClient = getAuthClient();

      if (!userInfo) {
        if (hash) {
          authClient.token
            .getWithoutPrompt({
              responseType: 'id_token',
              sessionToken: hash.split('#').pop(),
            })
            .then(({ tokens }) => {
              authClient.tokenManager.setTokens(tokens);
              userToolBoxJwt.set(tokens.idToken.idToken);
              getUserInfo().then(() => {
                const dataLayerData = {
                  event: 'login event',
                  form_type: 'social',
                };

                dataLayer.push(dataLayerData);
                Cookies.remove('redirect');
                navigate(redirect || '/toolbox');
              });
            });
        } else if (userToolBoxJwt.get()) {
          getUserInfo().then();
        }
      }
    });
  }, []);

  const handleForgotPassword = async (email) => {
    await forgotPassword(email);
    snackbar('Please check your email to reset password');
  };

  const onLogOut = async (onSuccess = () => {}) => {
    const client = getAuthClient();
    await client.revokeAccessToken();
    client.closeSession().then(() => {
      userToolBoxJwt.remove();
      setUserInfo(null);
      userToolBoxJwt.remove('ToolboxUserInfo');
      onSuccess();
    });
  };

  const seo = createSEO(contentfulSeo, props);
  const subNavData = toolboxDetailSubNav();

  return (
    <UserContext.Provider
      value={{
        userInfo,
        setUserInfo,
        openLoginModal,
        onLogOut,
      }}
    >
      <LayoutContainer className="toolbox-layout">
        <WebsiteLayout backgroundColor={backgroundColor} headerTheme={theme} {...props}>
          <Helmet>
            <script
              src="https://global.oktacdn.com/okta-auth-js/5.4.3/okta-auth-js.polyfill.js"
              type="text/javascript"
            />
            <script
              src="https://global.oktacdn.com/okta-auth-js/5.4.3/okta-auth-js.min.js"
              type="text/javascript"
            />
          </Helmet>
          <SEO {...seo} />
          {children}
          <SignupModal
            socialAuth={socialAuth}
            auth={auth}
            isModalOpen={isSignupModalOpen}
            handleCloseModal={handleCloseAuthModal}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            setUserInfo={setUserInfo}
            authClient={getAuthClient()}
            openLoginModal={openLoginModal}
          />
          <LoginModal
            socialAuth={socialAuth}
            auth={auth}
            isModalOpen={isLoginModalOpen}
            handleCloseModal={handleCloseAuthModal}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            setUserInfo={setUserInfo}
            authClient={getAuthClient()}
            openSignupModal={openSignupModal}
            handleForgotPassword={handleForgotPassword}
          />
          <SubNav
            data={subNavData}
            openLoginModal={openLoginModal}
            openSignupModal={openSignupModal}
            userInfo={userInfo}
            onLogOut={() => onLogOut()}
            location={props.location}
          />
        </WebsiteLayout>
      </LayoutContainer>
    </UserContext.Provider>
  );
};

const LayoutContainer = styled.div`
  .main-container {
    padding: 0;
  }
  .section-layout--container {
    overflow: inherit;
  }
  .bookmark-icon {
    display: flex !important;
  }
  @media (min-width: 1200px) and (max-width: 1378px) {
    margin-left: 80px;
    .website-header {
      padding-right: 80px;
    }
  }

  @media (max-width: 1240px) {
    .website-header {
      position: fixed;
    }
  }
`;

export default LocationWrapper((props) => ToolboxLayout(props));
